import React, { useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Alert,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Input,
} from "reactstrap";
import {
  getBusinessUsers,
  getBusinessUserPermissions,
  deleteBusinessUser,
} from "../../ApiCalls/businessUsers";
import UserFormModal from "./UserFormModal";
import "./styles/businessUsersTab.scss";

function BusinessUsersTab({ mantisBusinessId, token, requesterId }) {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setUsers(await getBusinessUsers(token, requesterId, mantisBusinessId));
    } catch (err) {
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token, requesterId, mantisBusinessId]);

  const fetchPermissions = useCallback(async () => {
    try {
      setPermissions(await getBusinessUserPermissions(token, requesterId));
    } catch (err) {
      console.error("Failed to load permissions:", err);
    }
  }, [token, requesterId]);

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, [fetchUsers, fetchPermissions]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingUserId(userId);
      setError("");
      await deleteBusinessUser(token, requesterId, mantisBusinessId, userId);
      setSuccess("User deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to delete user");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleUserSaved = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setSuccess("User saved successfully");
    setTimeout(() => setSuccess(""), 3000);
    fetchUsers();
  };

  const getRoleLabel = (user) => {
    if (user.mainUser) return "ADMIN";
    if (user.supervisorUser) return "SUPERVISOR";
    return "USER";
  };

  const getRoleBadgeColor = (user) => {
    if (user.mainUser) return "danger";
    if (user.supervisorUser) return "warning";
    return "secondary";
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="business-users-tab">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="business-users-tab">
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      <div className="users-header">
        <div className="header-left">
          <h5>👥 Team Members</h5>
          <small className="text-muted">{filteredUsers.length} user(s)</small>
        </div>
        <Button color="primary" onClick={handleCreateUser}>
          <i className="fas fa-user-plus" /> Add User
        </Button>
      </div>

      {/* Search Input */}
      <Row className="mb-4">
        <Col xs={12}>
          <div className="search-wrapper">
            <i className="fas fa-search" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </Col>
      </Row>

      {filteredUsers.length === 0 ? (
        <Alert color="info">
          {searchTerm
            ? "No users match your search"
            : "No users yet. Click 'Add User' to create your first team member."}
        </Alert>
      ) : (
        <div className="users-table-container">
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Position</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user._id} className={user.isDeleted ? "deleted-user" : ""}>
                  <td>
                    <strong>
                      {user.firstName} {user.lastName}
                    </strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <Badge color={getRoleBadgeColor(user)}>
                      {getRoleLabel(user)}
                    </Badge>
                  </td>
                  <td className="text-muted">{user.position || "—"}</td>
                  <td>
                    {user.isDeleted ? (
                      <Badge color="danger">Deleted</Badge>
                    ) : user.emailVerified ? (
                      <Badge color="success">Verified</Badge>
                    ) : (
                      <Badge color="warning">Pending</Badge>
                    )}
                  </td>
                  <td className="text-muted small">
                    {user.dateCreated
                      ? new Date(user.dateCreated).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button
                        size="sm"
                        color="info"
                        onClick={() => handleEditUser(user)}
                        disabled={user.isDeleted}
                      >
                        <i className="fas fa-edit" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteUser(user._id)}
                        disabled={deletingUserId === user._id || user.isDeleted}
                      >
                        {deletingUserId === user._id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <i className="fas fa-trash" /> Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* User Form Modal */}
      <UserFormModal
        isOpen={showUserModal}
        toggle={() => setShowUserModal(false)}
        user={editingUser}
        mantisBusinessId={mantisBusinessId}
        token={token}
        requesterId={requesterId}
        permissions={permissions}
        onSave={handleUserSaved}
      />
    </div>
  );
}

export default BusinessUsersTab;
